import React, {useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {makeStyles} from '@material-ui/core/styles';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Collapse from '@material-ui/core/Collapse';
import Typography from '@material-ui/core/Typography';
import {
    CheckCircleOutlined,
    ExpandLessOutlined,
    ExpandMoreOutlined,
    FolderOpenOutlined,
    FolderOutlined,
    HelpOutlineOutlined,
    PersonOutlined,
    RemoveCircleOutlineOutlined
} from '@material-ui/icons';
import {montarArvore} from './arvoreDocumentos.js';

const useStyles = makeStyles(theme => ({
    root: {
        padding: 0
    },
    grupo: {
        fontWeight: 500
    },
    pessoa: {
        paddingLeft: theme.spacing(4)
    },
    documento: {
        paddingLeft: theme.spacing(8),
        '&.Mui-selected': {
            backgroundColor: theme.palette.secondary.light,
            fontWeight: 500
        }
    },
    documentoSelecionado: {
        fontWeight: 500
    },
    icone: {
        minWidth: theme.spacing(4.5),
        color: theme.palette.secondary.dark
    },
    iconeAvaliados: {
        color: theme.palette.quinary.main
    },
    iconeNegados: {
        color: theme.palette.error.main
    },
    iconeSemAvaliacao: {
        color: theme.palette.senary.dark
    },
    vazio: {
        padding: theme.spacing(1, 2, 1, 4),
        color: theme.palette.secondary.dark,
        fontStyle: 'italic'
    }
}));

const CLASSE_ICONE_GRUPO = {
    AVALIADOS: 'iconeAvaliados',
    NEGADOS: 'iconeNegados',
    SEM_AVALIACAO: 'iconeSemAvaliacao'
};

const ICONE_DOCUMENTO_GRUPO = {
    AVALIADOS: CheckCircleOutlined,
    NEGADOS: RemoveCircleOutlineOutlined,
    SEM_AVALIACAO: HelpOutlineOutlined
};

/*
 * Árvore de documentos da avaliação: situação → pessoa → documento.
 * Tudo começa expandido; o clique no cabeçalho recolhe/expande e o clique no
 * documento avisa o pai pelo id do anexo.
 */
export default function ArvoreDocumentos({documentos, idSelecionado, onSelecionar}) {
    const classes = useStyles();
    const [recolhidos, setRecolhidos] = useState({});
    const arvore = useMemo(() => montarArvore(documentos), [documentos]);

    const alternar = chave => () => setRecolhidos(anterior => ({...anterior, [chave]: !anterior[chave]}));

    return (
        <List component='nav' dense className={classes.root} aria-label='Documentos da inscrição'>
            {arvore.map(grupo => {
                const recolhido = !!recolhidos[grupo.chave];
                const total = grupo.pessoas.reduce((soma, pessoa) => soma + pessoa.documentos.length, 0);
                const IconeDocumento = ICONE_DOCUMENTO_GRUPO[grupo.chave];

                return (
                    <React.Fragment key={grupo.chave}>
                        <ListItem button onClick={alternar(grupo.chave)}>
                            <ListItemIcon className={clsx(classes.icone, classes[CLASSE_ICONE_GRUPO[grupo.chave]])}>
                                {recolhido ? <FolderOutlined/> : <FolderOpenOutlined/>}
                            </ListItemIcon>
                            <ListItemText
                                primary={`${grupo.titulo} (${total})`}
                                primaryTypographyProps={{className: classes.grupo}}/>
                            {recolhido ? <ExpandMoreOutlined/> : <ExpandLessOutlined/>}
                        </ListItem>
                        <Collapse in={!recolhido} timeout='auto' unmountOnExit>
                            {!grupo.pessoas.length && (
                                <Typography variant='body2' className={classes.vazio}>
                                    Nenhum documento.
                                </Typography>
                            )}
                            <List component='div' disablePadding dense>
                                {grupo.pessoas.map(pessoa => {
                                    const pessoaRecolhida = !!recolhidos[pessoa.chave];

                                    return (
                                        <React.Fragment key={pessoa.chave}>
                                            <ListItem button className={classes.pessoa} onClick={alternar(pessoa.chave)}>
                                                <ListItemIcon className={classes.icone}>
                                                    <PersonOutlined/>
                                                </ListItemIcon>
                                                <ListItemText primary={pessoa.titulo}/>
                                                {pessoaRecolhida ? <ExpandMoreOutlined/> : <ExpandLessOutlined/>}
                                            </ListItem>
                                            <Collapse in={!pessoaRecolhida} timeout='auto' unmountOnExit>
                                                <List component='div' disablePadding dense>
                                                    {pessoa.documentos.map(documento => (
                                                        <ListItem
                                                            button
                                                            key={documento.idInscricaoAnexo}
                                                            className={classes.documento}
                                                            selected={documento.idInscricaoAnexo === idSelecionado}
                                                            onClick={() => onSelecionar(documento.idInscricaoAnexo)}>
                                                            <ListItemIcon className={clsx(classes.icone, classes[CLASSE_ICONE_GRUPO[grupo.chave]])}>
                                                                <IconeDocumento fontSize='small'/>
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={documento.titulo}
                                                                primaryTypographyProps={{
                                                                    className: documento.idInscricaoAnexo === idSelecionado
                                                                        ? classes.documentoSelecionado
                                                                        : undefined
                                                                }}/>
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </Collapse>
                                        </React.Fragment>
                                    );
                                })}
                            </List>
                        </Collapse>
                    </React.Fragment>
                );
            })}
        </List>
    );
}

ArvoreDocumentos.propTypes = {
    documentos: PropTypes.array.isRequired,
    idSelecionado: PropTypes.number,
    onSelecionar: PropTypes.func.isRequired
};
